import React from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { message, Card } from "antd";
import { useUpdateSessionName } from "../../api/SessionsAPI";
import EditableName from "../common/EditableName";
import {
  agGridThemeVars,
  agGridDefaultColDef,
  agGridPinnedActionsCellStyle,
} from "../../constants/agGridTheme";

const ParticipantsTable = ({
  participants,
  isLoading,
  error,
  totalCompetenciesInDb,
}) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const updateSessionNameMutation = useUpdateSessionName();

  const openReport = (sessionId) => {
    if (!currentUser?.id) {
      console.error('No valid session found');
      return;
    }

    navigate(`/reports/${sessionId}`);
  };

  const dateFormatter = (params) => {
    if (!params.value) return "";
    return new Date(params.value).toLocaleString();
  };

  const actionRenderer = (params) => {
    const isCompleted = params.data.status === "completed";

    return (
      <div className="flex items-center justify-center w-full h-full">
        <button
          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors duration-200 shadow-sm flex items-center justify-center h-7 ${
            isCompleted
              ? "bg-primary hover:bg-primaryLight text-white hover:shadow"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
          onClick={() => isCompleted && openReport(params.data.id)}
          disabled={!isCompleted}
          title={
            isCompleted
              ? "View participant report"
              : "Report available after completion"
          }
        >
          <span className="text-center">View Report</span>
        </button>
      </div>
    );
  };

  const columnDefs = [
    {
      field: "name",
      headerName: "Session Name",
      flex: 3,
      minWidth: 180,
      cellRenderer: (params) => (
        <EditableName
          value={params.data.name || params.data.display_name}
          fallback="Untitled Cohort Session"
          title="Edit cohort session name"
          inputLabel="Session name"
          buttonVariant="icon"
          buttonAriaLabel="Edit cohort session name"
          buttonClassName="!text-primary hover:!text-primaryLight"
          onSave={(name) =>
            new Promise((resolve, reject) => {
              updateSessionNameMutation.mutate(
                { sessionId: params.data.id, name },
                {
                  onSuccess: () => {
                    message.success("Session name updated");
                    resolve();
                  },
                  onError: (error) => {
                    message.error(error.response?.data?.error || "Failed to update session name");
                    reject(error);
                  },
                }
              );
            })
          }
        />
      ),
    },
    {
      field: "username",
      headerName: "Participant",
      flex: 4,
      minWidth: 120,
      cellRenderer: (params) => {
        const { username } = params.data;
        return (
          <div className="flex items-center">
            <span className="font-medium">{username}</span>
          </div>
        );
      },
    },
    {
      field: "total_competencies",
      headerName: "Competencies",
      flex: 1.5,
      minWidth: 110,
      cellRenderer: (params) => {
        const { completed_competencies } = params.data;
        const percentage = Number(((completed_competencies / totalCompetenciesInDb) * 100).toFixed(3));
        return `${completed_competencies} / ${totalCompetenciesInDb} (${percentage}%)`;
      },
    },
    {
      field: "is_completed",
      headerName: "Status",
      flex: 1.5,
      minWidth: 100,
      cellRenderer: (params) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            params.value
              ? "bg-green-100 text-green-800"
              : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {params.value ? "Completed" : "In Progress"}
        </span>
      ),
    },
    {
      headerName: "Joined",
      field: "created_at",
      valueFormatter: dateFormatter,
      flex: 2,
      minWidth: 150,
    },
    {
      headerName: "Actions",
      cellRenderer: actionRenderer,
      pinned: "right",
      sortable: false,
      filter: false,
      width: 150,
      cellStyle: agGridPinnedActionsCellStyle,
    },
  ];

  if (isLoading) {
    return <div className="text-center py-4">Loading participants...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-4 text-red-500">
        Error loading participants: {error.message}
      </div>
    );
  }

  if (!participants || participants.length === 0) {
    return (
      <div className="text-center py-4">
        No participants have joined this cohort yet.
      </div>
    );
  }

  return (
    <Card className="w-full">
      <div
        className="ag-theme-alpine w-full"
        style={{
          height: 400,
          ...agGridThemeVars,
        }}
      >
        <AgGridReact
          rowData={participants}
          columnDefs={columnDefs}
          defaultColDef={agGridDefaultColDef}
          pagination={true}
          paginationPageSize={10}
          domLayout="autoHeight"
          sortingOrder={["desc", "asc", null]}
          initialSort={[
            {
              colId: "status",
              sort: "desc",
            },
          ]}
        />
      </div>
    </Card>
  );
};

export default ParticipantsTable;